/**
 * size-guide.js — Size Guide popup controller
 *
 * Wires [data-size-guide-trigger] (in variant-picker.liquid) to
 * [data-size-guide-modal] (snippets/size-guide-modal.liquid).
 *
 * Behavior:
 *   - Click on trigger → showModal()
 *   - Click on close button, ESC, or backdrop click → close()
 *   - Garment tabs (Half sleeve / Full sleeve / Resort) swap which table
 *     is visible. APG tabs pattern: roving tabindex, Left/Right arrow keys
 *     wrap, selection follows focus, inactive panels use the `hidden`
 *     attribute so they leave the accessibility tree.
 *   - Inch / Cm toggle rewrites every measurement cell in every table.
 *     Inches are canonical (data-in on each <td>) and print verbatim;
 *     centimetres are derived as inches × 2.54 to one decimal place.
 *   - The two controls are independent: switching garment never touches cell
 *     text, and a unit switch rewrites hidden tables too, so each selection
 *     survives the other.
 *   - Body scroll lock via .sg-modal-open while open.
 *   - Focus management: close button receives focus on open; trigger receives
 *     focus on close.
 */

(function () {
  'use strict';

  const dialog = document.querySelector('[data-size-guide-modal]');
  if (!dialog) return;

  let lastTrigger = null;

  function openModal(trigger) {
    if (typeof dialog.showModal !== 'function') return; // graceful: ignore on legacy browsers
    if (dialog.open) return;
    lastTrigger = trigger || null;
    dialog.showModal();
    document.body.classList.add('sg-modal-open');

    // Move focus to close button
    const closeBtn = dialog.querySelector('[data-size-guide-close]');
    if (closeBtn) closeBtn.focus();
  }

  function closeModal() {
    if (!dialog.open) return;
    dialog.close();
  }

  // ── Open delegate ───────────────────────────────────────────
  document.addEventListener('click', function (e) {
    const trigger = e.target.closest('[data-size-guide-trigger]');
    if (trigger) {
      e.preventDefault();
      openModal(trigger);
    }
  });

  // ── Close delegate (close button + backdrop) ────────────────
  dialog.addEventListener('click', function (e) {
    // Close button
    if (e.target.closest('[data-size-guide-close]')) {
      closeModal();
      return;
    }
    // Backdrop click — when target IS the dialog itself (panel is the inner div)
    if (e.target === dialog) {
      closeModal();
    }
  });

  // ── On close (ESC or programmatic) ──────────────────────────
  dialog.addEventListener('close', function () {
    document.body.classList.remove('sg-modal-open');
    if (lastTrigger && typeof lastTrigger.focus === 'function') {
      lastTrigger.focus();
    }
    lastTrigger = null;
  });

  // ── Unit toggle (Inch / Cm) ─────────────────────────────────
  // Each <td> carries data-in with the merchant's canonical INCH value.
  // The inch view prints that string verbatim — no arithmetic, so 16.5 can
  // never render as 16.4 or 16.50. The cm view is derived.
  const dataCells = dialog.querySelectorAll('[data-size-guide-table] td[data-in]');

  // inches × 2.54, rounded to 1 decimal. Done in integer tenths because the
  // float path drifts: 17.5 * 2.54 === 44.449999999999996, which would round
  // down to 44.4 instead of 44.5.
  function inchesToCm(raw) {
    const tenths = Math.round(parseFloat(raw) * 10);
    if (!Number.isFinite(tenths)) return null;
    const cmTenths = Math.round((tenths * 254) / 100);
    const cm = cmTenths / 10;
    return cmTenths % 10 === 0 ? String(cm) : cm.toFixed(1);
  }

  function renderCells(unit) {
    dataCells.forEach(function (td) {
      const raw = td.getAttribute('data-in');
      if (unit === 'cm') {
        const cm = inchesToCm(raw);
        if (cm !== null) td.textContent = cm;
      } else {
        td.textContent = raw;
      }
    });
  }

  const unitBtns = dialog.querySelectorAll('[data-size-guide-unit]');
  unitBtns.forEach(function (btn) {
    btn.addEventListener('click', function () {
      const unit = btn.dataset.sizeGuideUnit;
      unitBtns.forEach(function (b) {
        const isActive = b === btn;
        b.classList.toggle('is-active', isActive);
        b.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      dialog.dataset.activeUnit = unit;
      renderCells(unit);
    });
  });

  // Default to inches on first open (matches initial active state).
  dialog.dataset.activeUnit = 'in';
  renderCells('in');

  // ── Garment tabs (Half sleeve / Full sleeve / Resort) ───────
  // No product data identifies garment type, so the shopper picks the chart.
  const garmentTabs = Array.prototype.slice.call(
    dialog.querySelectorAll('[data-size-guide-garment]')
  );
  const garmentPanels = Array.prototype.slice.call(
    dialog.querySelectorAll('[data-size-guide-panel]')
  );

  function selectGarment(garment, moveFocus) {
    garmentTabs.forEach(function (tab) {
      const isActive = tab.dataset.sizeGuideGarment === garment;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
      if (isActive && moveFocus) tab.focus();
    });
    garmentPanels.forEach(function (panel) {
      panel.hidden = panel.dataset.sizeGuidePanel !== garment;
    });
    dialog.dataset.activeGarment = garment;
  }

  garmentTabs.forEach(function (tab, index) {
    tab.addEventListener('click', function () {
      selectGarment(tab.dataset.sizeGuideGarment, false);
    });

    tab.addEventListener('keydown', function (e) {
      let nextIndex = null;
      if (e.key === 'ArrowRight') {
        nextIndex = (index + 1) % garmentTabs.length;
      } else if (e.key === 'ArrowLeft') {
        nextIndex = (index - 1 + garmentTabs.length) % garmentTabs.length;
      }
      if (nextIndex === null) return;
      e.preventDefault();
      selectGarment(garmentTabs[nextIndex].dataset.sizeGuideGarment, true);
    });
  });

  // Normalize state from the markup's initial selection.
  if (garmentTabs.length) {
    const initial =
      garmentTabs.filter(function (t) {
        return t.getAttribute('aria-selected') === 'true';
      })[0] || garmentTabs[0];
    selectGarment(initial.dataset.sizeGuideGarment, false);
  }
})();
